// Considered: SpherePickerDialogOrganism — rejected because it hard-codes the subscribe
// commit (subscribeSphereToChannel+409), channel exclusion, scope segment, and subscribe
// i18n; deploy must not import channel semantics. Extraction (move, not fork) is the
// anti-fork move — the subscribe organism shrinks to a thin wrapper. See spec Reuse Audit.
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { FilterExpression } from 'types/filters';
import { useSphereSearch } from '../../../hooks/useSphereSearch';
import type { SphereSearchResult } from '../../../hooks/useSphereSearch';
import CustomDialogAtom from '../../atoms/CustomDialog/CustomDialogAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import SphereRowMolecule from '../../molecules/SphereRow/SphereRowMolecule';
import type { SphereRowCenterInfo } from '../../molecules/SphereRow/SphereRowMolecule';
import SphereRowHeaderMolecule from '../../molecules/SphereRow/SphereRowHeaderMolecule';
import ContextSelectorMenuMolecule from '../../molecules/ContextSelectorMenu/ContextSelectorMenuMolecule';
import type { ContextOption } from '../../molecules/ContextSelectorMenu/ContextSelectorMenuMolecule';
import { listContexts } from '../../../api/contextApi';
import type { Context } from 'types/context';
import SourceTabsMolecule, { SourceMode } from '../../molecules/marketplace/SourceTabsMolecule';
import SphereFamilyTypePickerMolecule from '../../molecules/SphereFamilyTypePicker/SphereFamilyTypePickerMolecule';
import type { Schema } from 'types/schema';
import type { GlyphColor } from '../../../types/glyph';
import './SphereSelectDialogOrganism.scss';

export interface SphereSelectDialogOrganismProps {
  open: boolean;
  onClose: () => void;
  /** i18n key for the dialog title */
  titleKey: string;
  subtitleKey?: string;
  /**
   * Called when the user commits a selection. Returning false (or a rejected/false promise)
   * keeps the dialog open so the wrapper can show an error. Returning true closes the dialog.
   */
  onSelect: (sphere: SphereSearchResult) => Promise<boolean> | boolean;
  /** Sphere IDs excluded from the rendered result list */
  excludedSphereIds?: string[];
  /**
   * When set, renders a localized highlight chip on the matching row.
   * Used by the deploy dialog to mark the current sphere.
   */
  highlightSphereId?: string;
  /** When provided, used directly as contextFilters in the search call */
  fixedContextFilters?: FilterExpression[];
  /** Set to false to hide the context selector (e.g. deploy dialog with fixed scope) */
  showContextSelector?: boolean;
  /** Contexts populated in the context selector when shown */
  availableContexts?: ContextOption[];
  /**
   * System name of the default context. Defaults to 'center_hierarchy_context'.
   * The context's level0PropertyFilters scope the directory.
   */
  defaultContextSystemName?: string;
  /**
   * Slot rendered at the top of the dialog body, above the search bar.
   * Subscribe wrapper uses it for the scope segment bar; deploy wrapper for ApplicabilitySummary.
   */
  headerExtras?: React.ReactNode;
  /**
   * Permission level passed to useSphereSearch.
   * Default: 'edit'. Stricter than useSphereSearch's own 'view' default — both subscribe
   * and deploy require edit access on the target sphere.
   */
  permission?: 'view' | 'edit';
  /** Sphere ID used as the anchor for the Nearby tab */
  nearbyAnchorSphereId?: string;
  /** Disables row selection while the wrapper is committing */
  busy?: boolean;
  /** Optional error node rendered at the bottom of the dialog body (e.g. 409 duplicate message) */
  errorNode?: React.ReactNode;
}

function sphereDisplayName(s: SphereSearchResult): string {
  return s.properties?.name || s.label;
}

function computeAmbiguousNames(spheres: SphereSearchResult[]): Set<string> {
  const counts = new Map<string, number>();
  for (const s of spheres) {
    const name = sphereDisplayName(s);
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return new Set([...counts.entries()].filter(([, c]) => c > 1).map(([n]) => n));
}

function contextToOption(ctx: Context): ContextOption {
  const levels = ctx.definition?.levels;
  const level0Filters = levels && levels.length > 0 ? levels[0]?.filters?.propertyFilters ?? [] : [];
  return {
    systemName: ctx.system_name,
    name: ctx.name,
    description: ctx.description,
    level0PropertyFilters: level0Filters,
  };
}

const EMPTY_CONTEXTS: ContextOption[] = [];

function toRowCenterInfo(center: SphereSearchResult['center']): SphereRowCenterInfo | null {
  if (!center) return null;
  return {
    sphereId: center.id,
    sphereName: center.name,
    glyph: center.glyph ?? undefined,
    glyphColor: center.glyphColor as unknown as GlyphColor | undefined,
  };
}

const SphereSelectDialogOrganism: React.FC<SphereSelectDialogOrganismProps> = ({
  open,
  onClose,
  titleKey,
  subtitleKey,
  onSelect,
  excludedSphereIds = [],
  highlightSphereId,
  fixedContextFilters,
  showContextSelector = true,
  availableContexts = EMPTY_CONTEXTS,
  defaultContextSystemName = 'center_hierarchy_context',
  headerExtras,
  permission = 'edit',
  nearbyAnchorSphereId,
  busy = false,
  errorNode,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [searchTerm, setSearchTerm] = useState('');
  const [source, setSource] = useState<SourceMode>('all');
  const [typeFilter, setTypeFilter] = useState<FilterExpression[]>([]);
  const [committing, setCommitting] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const [selectedContext, setSelectedContext] = useState<string | null>(defaultContextSystemName);
  const [fetchedContexts, setFetchedContexts] = useState<ContextOption[]>([]);
  const [openCount, setOpenCount] = useState(0);
  const [sortCol, setSortCol] = useState<'name' | 'description' | 'center' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [orgSchemas, setOrgSchemas] = useState<Schema[]>([]);

  const effectiveContexts = availableContexts.length > 0 ? availableContexts : fetchedContexts;

  const searchInputRef = useRef<HTMLInputElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isFirstOpenRef = useRef(true);

  const excludedSet = useMemo(() => new Set(excludedSphereIds), [excludedSphereIds]);

  // When fixedContextFilters is provided it takes precedence; otherwise derive from selected context.
  const contextFilters = useMemo<FilterExpression[]>(() => {
    if (fixedContextFilters !== undefined) return fixedContextFilters;
    if (!selectedContext) return [];
    const ctx = effectiveContexts.find((c) => c.systemName === selectedContext);
    return ctx?.level0PropertyFilters ?? [];
  }, [fixedContextFilters, selectedContext, effectiveContexts]);

  // SphereFamilyTypePickerMolecule also emits a `label` entry for the family
  // axis — that's a client-side narrowing aid over which types are shown,
  // never a search constraint. Only the `type` entries are a real filter;
  // keep the full value (incl. `label`) round-tripping through the picker
  // so its family-pill active state persists.
  const searchTypeFilter = useMemo(
    () => typeFilter.filter((f) => f.property === 'type'),
    [typeFilter]
  );

  useEffect(() => {
    if (!open || !showContextSelector) return;
    if (availableContexts.length > 0) return;
    const schemaClient = apiClients?.schemaClient;
    if (!schemaClient) return;
    let cancelled = false;
    listContexts(schemaClient)
      .then((ctxs) => {
        if (!cancelled) setFetchedContexts(ctxs.map(contextToOption));
      })
      .catch(() => {
        if (!cancelled) setFetchedContexts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, showContextSelector, availableContexts, apiClients]);

  // Org-wide schema list for the family/type picker — real Schema objects, so
  // system_name prefixing always matches (fixes the DEFAULT_FAMILIES prod bug
  // where a hardcoded family→schema map used unprefixed names).
  useEffect(() => {
    if (!open) return;
    const schemaClient = apiClients?.schemaClient;
    if (!schemaClient || typeof schemaClient.get !== 'function') return;
    let cancelled = false;
    schemaClient
      // Org scope is derived server-side from the token; passing it from the
      // client was removed upstream as an authorization bypass.
      .get('/schemas')
      .then((response: unknown) => {
        if (cancelled) return;
        // Accept a bare array or `{ data: [...] }`; anything else is "no schemas". A
        // `{ data: {} }` body used to reach the family picker as `{}` and throw on
        // `.map` inside the dialog's render (EAINT-908 host-frame finding).
        const list = Array.isArray(response) ? response : (response as { data?: unknown } | null)?.data;
        setOrgSchemas(Array.isArray(list) ? (list as Schema[]) : []);
      })
      .catch(() => {
        if (!cancelled) setOrgSchemas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, apiClients]);

  const {
    results: rawResults,
    loading,
    loadingMore,
    hasNextPage,
    setQuery,
    loadMore,
  } = useSphereSearch({
    source,
    contextFilters: contextFilters.length > 0 ? contextFilters : undefined,
    filterExpressions: searchTypeFilter.length > 0 ? searchTypeFilter : undefined,
    permission,
    nearbyAnchorSphereId,
    resetKey: openCount,
  });

  const displayResults = useMemo(() => rawResults.filter((s) => !excludedSet.has(s.id)), [rawResults, excludedSet]);

  const ambiguousNames = useMemo(() => computeAmbiguousNames(displayResults), [displayResults]);

  const handleSort = useCallback((col: 'name' | 'description' | 'center') => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return col;
      }
      setSortDir('asc');
      return col;
    });
    setFocusIdx(0);
  }, []);

  const sortedResults = useMemo(() => {
    if (!sortCol) return displayResults;
    return [...displayResults].sort((a, b) => {
      let va = '';
      let vb = '';
      if (sortCol === 'name') {
        va = sphereDisplayName(a).toLowerCase();
        vb = sphereDisplayName(b).toLowerCase();
      } else if (sortCol === 'description') {
        va = (a.description ?? '').toLowerCase();
        vb = (b.description ?? '').toLowerCase();
      } else if (sortCol === 'center') {
        va = (a.center?.name ?? '').toLowerCase();
        vb = (b.center?.name ?? '').toLowerCase();
      }
      const cmp = va.localeCompare(vb);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [displayResults, sortCol, sortDir]);

  const hasNextPageRef = useRef(hasNextPage);
  hasNextPageRef.current = hasNextPage;
  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPageRef.current && !loadingMoreRef.current) {
          loadMoreRef.current();
        }
      },
      { root, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, loading]);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSource('all');
      setTypeFilter([]);
      setCommitting(false);
      setFocusIdx(0);
      if (showContextSelector !== false) {
        setSelectedContext(defaultContextSystemName);
      }
      setQuery('');
      if (!isFirstOpenRef.current) {
        setOpenCount((c) => c + 1);
      }
      isFirstOpenRef.current = false;
    }
    // defaultContextSystemName/showContextSelector are intentionally stable props; setQuery doesn't change.
  }, [open]);

  useEffect(() => {
    if (open && !loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open, loading]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      setQuery(value);
    },
    [setQuery]
  );

  const handleSourceChange = useCallback(
    (newSource: SourceMode) => {
      setSource(newSource);
      setSearchTerm('');
      setQuery('');
    },
    [setQuery]
  );

  const handleTypeFilterChange = useCallback((filters: FilterExpression[]) => {
    setTypeFilter(filters);
  }, []);

  const handleContextChange = useCallback((systemName: string | null) => {
    setSelectedContext(systemName);
  }, []);

  const handleCommit = useCallback(
    async (sphere: SphereSearchResult) => {
      if (committing || busy) return;
      setCommitting(true);
      try {
        const result = await onSelect(sphere);
        if (result !== false) {
          onClose();
        }
      } finally {
        setCommitting(false);
      }
    },
    [committing, busy, onSelect, onClose]
  );

  const isDisabled = committing || busy;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isDisabled) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((i) => Math.min(i + 1, sortedResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && sortedResults[focusIdx]) {
        e.preventDefault();
        handleCommit(sortedResults[focusIdx]);
      }
    },
    [isDisabled, sortedResults, focusIdx, handleCommit]
  );

  const handleClose = useCallback(() => {
    if (isDisabled) return;
    onClose();
  }, [isDisabled, onClose]);

  const isNoMatch = !loading && displayResults.length === 0 && !!searchTerm;
  const isNothingAvailable = !loading && rawResults.length === 0 && !searchTerm;

  const totalCount = rawResults.length;

  const dialogHeader = (
    <div className="sphere-select-dialog__header-content">
      <span className="sphere-select-dialog__title">{t(titleKey)}</span>
      {subtitleKey && <span className="sphere-select-dialog__subtitle">{t(subtitleKey)}</span>}
      <span className="sphere-select-dialog__flex-gap" />
      <button type="button" className="sphere-select-dialog__close-btn" onClick={handleClose} disabled={isDisabled} aria-label={t('sphereSelect.cancel')}>
        <i className="pi pi-times" aria-hidden="true" />
      </button>
    </div>
  );

  const dialogFooter = (
    <div className="sphere-select-dialog__footer">
      <span className="sphere-select-dialog__result-count" aria-live="polite" aria-atomic="true">
        {!loading && !isNoMatch && !isNothingAvailable && totalCount > 0 ? t('sphereSelect.resultCount', { count: totalCount }) : null}
      </span>
      <span className="sphere-select-dialog__flex-gap" />
      <span className="sphere-select-dialog__keyboard-hint">{t('sphereSelect.keyboardHint')}</span>
      <button type="button" className="clear-button" onClick={handleClose} disabled={isDisabled}>
        {t('sphereSelect.cancel')}
      </button>
    </div>
  );

  const renderRow = (sphere: SphereSearchResult, globalIndex: number) => {
    // uses sortedResults ordering — globalIndex corresponds to sorted position
    const isHighlighted = !!highlightSphereId && sphere.id === highlightSphereId;
    const highlightChip = isHighlighted ? <ChipAtom label={t('sphereSelect.currentSphere')} className="sphere-select-dialog__highlight-chip" /> : undefined;

    return (
      <SphereRowMolecule
        key={sphere.id}
        variant="table"
        sphereId={sphere.id}
        sphereName={sphereDisplayName(sphere)}
        imageId={sphere.properties?.imageId}
        glyph={sphere.glyph}
        glyphColor={sphere.glyphColor as unknown as GlyphColor}
        isPrivate={sphere.isPrivate}
        status={sphere.status}
        description={sphere.description}
        center={toRowCenterInfo(sphere.center)}
        reason={source === 'all' ? sphere.reason : undefined}
        ambiguous={ambiguousNames.has(sphereDisplayName(sphere))}
        focused={globalIndex === focusIdx}
        onSelect={isDisabled ? undefined : () => handleCommit(sphere)}
        onFocus={() => setFocusIdx(globalIndex)}
        trailing={highlightChip}
      />
    );
  };

  return (
    <CustomDialogAtom
      header={dialogHeader}
      visible={open}
      onHide={handleClose}
      footer={dialogFooter}
      style={{ width: '1040px', maxWidth: '95vw' }}
      className="sphere-select-dialog"
      showCloseIcon={false}
    >
      <div className="sphere-select-dialog__body">
        {headerExtras}

        <div className="Search-input-box sphere-select-dialog__search">
          <i className="pi pi-search sphere-select-dialog__search-icon" aria-hidden="true" />
          <input
            ref={searchInputRef}
            type="text"
            className="sphere-select-dialog__search-input"
            placeholder={t('sphereChannels.spherePicker.searchPlaceholder')}
            aria-label={t('sphereChannels.spherePicker.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          {showContextSelector && effectiveContexts.length > 0 && (
            <div className="sphere-select-dialog__search-context">
              <ContextSelectorMenuMolecule contexts={effectiveContexts} selectedContext={selectedContext} onSelect={handleContextChange} />
            </div>
          )}
        </div>

        <div className="sphere-select-dialog__filter-row">
          <SourceTabsMolecule value={source} onChange={handleSourceChange} nearbyAnchorSphereId={nearbyAnchorSphereId} />
          <span className="sphere-select-dialog__filter-divider" aria-hidden="true" />
          <SphereFamilyTypePickerMolecule
            schemas={orgSchemas}
            value={typeFilter}
            onChange={handleTypeFilterChange}
            collapsible={false}
            defaultCollapsed={false}
            className="sphere-select-dialog__type-family-picker"
          />
        </div>
        <div className="sphere-select-dialog__list-container">
          {!loading && !isNoMatch && !isNothingAvailable && sortedResults.length > 0 && (
            <SphereRowHeaderMolecule sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
          )}

          {/* The listbox wraps the options and nothing else: a role="listbox" may only own
              role="option" children, and this scroller also carries the spinner, the empty
              states and the infinite-scroll sentinel (axe aria-required-children). Keyboard
              handling stays on the scroller, which is what owns the roving focus index. */}
          <div ref={listRef} className="sphere-select-dialog__list" aria-busy={loading} onKeyDown={handleKeyDown}>
            {loading && (
              <div className="sphere-select-dialog__loading">
                <i className="pi pi-spin pi-spinner" aria-label={t('sphereChannels.spherePicker.loading')} />
              </div>
            )}

            {!loading && isNothingAvailable && (
              <EmptyStateAtom
                icon="pi-circle-off"
                title={t('sphereChannels.spherePicker.nothingAvailable.title')}
                description={t('sphereChannels.spherePicker.nothingAvailable.description')}
                className="sphere-select-dialog__empty-state"
              />
            )}

            {!loading && isNoMatch && (
              <EmptyStateAtom
                icon="pi-search"
                title={t('sphereChannels.spherePicker.noMatch.title')}
                description={t('sphereChannels.spherePicker.noMatch.description', { query: searchTerm })}
                className="sphere-select-dialog__empty-state"
              />
            )}

            {!loading && sortedResults.length > 0 && (
              <div className="sphere-select-dialog__options" role="listbox" aria-label={t(titleKey)}>
                {sortedResults.map((sphere, i) => renderRow(sphere, i))}
              </div>
            )}

            {loadingMore && (
              <div className="sphere-select-dialog__loading-more">
                <i className="pi pi-spin pi-spinner" aria-hidden="true" />
              </div>
            )}

            <div ref={sentinelRef} className="sphere-select-dialog__sentinel" aria-hidden="true" />
          </div>
        </div>

        {errorNode}
      </div>
    </CustomDialogAtom>
  );
};

export default SphereSelectDialogOrganism;
